import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { eggState } from '../../lib/eggs';
import { HALL_WINDOWS } from './HallWindows';
import { groundHeight } from './Landscape';

// Decorations for today's festival (lib/festivals.js): lanterns under the glass roof, lamps or
// candles along the walls, kites or carp streamers over the lawn, pumpkins or painted eggs outside,
// a Christmas tree, and a flower carpet / rangoli on the floor. Petals, fireworks and balloons reuse
// the Easter-egg effects (EasterEggs.jsx) in the festival's colours.
//
// Cheap on purpose: each kind is one InstancedMesh (one draw call), and they all use the same two
// material types as the confetti and balloons (instanced MeshBasic double-sided, instanced
// MeshStandard), so a festival day compiles no extra shader programs. The decorations are chosen
// once when the gallery loads, so the shader prewarm covers them.

const rand = (a, b) => a + Math.random() * (b - a);
const noRaycast = () => null; // decorations never block clicks on the floor, paintings or windows
const colors = (list) => list.map((c) => new THREE.Color(c));

const mergeGeometries = (parts) => {
  const pos = [], nor = [];
  parts.forEach((g) => {
    const n = g.index ? g.toNonIndexed() : g;
    pos.push(...n.attributes.position.array);
    nor.push(...n.attributes.normal.array);
    g.dispose(); if (n !== g) n.dispose();
  });
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  return out;
};

// An instanced mesh of one geometry. `lit` picks MeshStandard (shaded, can glow) or MeshBasic.
const useInstanced = (makeGeometry, items, lit, extra = {}) => {
  const mesh = useMemo(() => {
    const material = lit
      ? new THREE.MeshStandardMaterial({ roughness: 0.3, metalness: 0.05, ...extra })
      : new THREE.MeshBasicMaterial({ side: THREE.DoubleSide });
    const m = new THREE.InstancedMesh(makeGeometry(), material, items.length);
    m.frustumCulled = false;
    m.raycast = noRaycast;
    const dummy = new THREE.Object3D();
    items.forEach((it, i) => {
      dummy.position.copy(it.pos);
      dummy.rotation.set(it.rx || 0, it.ry || 0, it.rz || 0);
      dummy.scale.setScalar(it.s || 1);
      dummy.updateMatrix();
      m.setMatrixAt(i, dummy.matrix);
      m.setColorAt(i, it.color);
    });
    return m;
  }, []); // built once: the items never change during a visit
  useEffect(() => () => { mesh.geometry.dispose(); mesh.material.dispose(); mesh.dispose(); }, [mesh]);
  return mesh;
};

const dummy = new THREE.Object3D();

// ------------------------------------------------------------------ lanterns under the roof
const lanternGeometry = (paper) => {
  if (paper) { // Tanabata: paper wish strips on a string
    const strip = new THREE.BoxGeometry(0.14, 0.55, 0.01); strip.translate(0, -0.3, 0);
    const str = new THREE.CylinderGeometry(0.004, 0.004, 1.2, 3); str.translate(0, 0.6, 0);
    return mergeGeometries([strip, str]);
  }
  const body = new THREE.SphereGeometry(0.3, 14, 10); body.scale(1, 0.85, 1);
  const top = new THREE.CylinderGeometry(0.13, 0.13, 0.06, 12); top.translate(0, 0.27, 0);
  const bottom = new THREE.CylinderGeometry(0.13, 0.13, 0.06, 12); bottom.translate(0, -0.27, 0);
  const tassel = new THREE.CylinderGeometry(0.015, 0.03, 0.25, 6); tassel.translate(0, -0.42, 0);
  const str = new THREE.CylinderGeometry(0.005, 0.005, 1.2, 3); str.translate(0, 0.9, 0);
  return mergeGeometries([body, top, bottom, tassel, str]);
};
const Lanterns = ({ palette, paper, nightRef, besideTree }) => {
  const items = useMemo(() => {
    const out = [], c = colors(palette);
    for (let i = 0; i < 12; i++) { // a ring around the statue
      const a = (i / 12) * Math.PI * 2 + 0.13;
      out.push({ pos: new THREE.Vector3(Math.cos(a) * 6.3, rand(4.7, 5.2), Math.sin(a) * 6.3), color: c[i % c.length] });
    }
    [[-8.6, -8.6], [8.6, -8.6], [-8.6, 8.6], [8.6, 8.6], [0, 9.2], [-9.2, 0], [9.2, 0]]
      .filter(([x, z]) => !(besideTree && x > 0 && z > 0)) // the Christmas tree stands in that corner
      .forEach(([x, z], i) => {
      out.push({ pos: new THREE.Vector3(x, rand(4.8, 5.2), z), color: c[(i + 1) % c.length] });
    });
    return out.map((it) => ({ ...it, ph: Math.random() * 6 }));
  }, [palette]);
  const mesh = useInstanced(() => lanternGeometry(paper), items, true, { emissive: new THREE.Color('#ff9a3c'), emissiveIntensity: 0.12 });
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    mesh.material.emissiveIntensity = 0.12 + 0.3 * nightRef.current; // they glow softly after dark
    items.forEach((it, i) => {
      dummy.position.copy(it.pos);
      dummy.rotation.set(Math.sin(t * 0.7 + it.ph) * 0.06, t * 0.15 + it.ph, Math.cos(t * 0.6 + it.ph) * 0.06);
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
  });
  return <primitive object={mesh} />;
};

// ------------------------------------------------------------------ lamps / candles along the walls
const lampSpots = () => {
  const out = [], E = 11.55;
  for (let v = -10.5; v <= 10.5; v += 1.5) {
    out.push([v, -E], [-E, v], [E, v]);
    if (Math.abs(v) > 1.8) out.push([v, E]); // leave the glass door clear
  }
  return out;
};
const Lamps = ({ candles }) => {
  const spots = useMemo(lampSpots, []);
  const base = useMemo(() => spots.map(([x, z]) => ({
    pos: new THREE.Vector3(x, 0, z),
    color: new THREE.Color(candles ? (Math.random() < 0.5 ? '#f1faee' : '#a8dadc') : '#b5562b'),
  })), [spots, candles]);
  const flames = useMemo(() => spots.map(([x, z]) => ({
    pos: new THREE.Vector3(x, candles ? 0.36 : 0.14, z), color: new THREE.Color('#ffc861'), ph: Math.random() * 10,
  })), [spots, candles]);
  const bowls = useInstanced(() => {
    if (candles) { const g = new THREE.CylinderGeometry(0.035, 0.035, 0.3, 8); g.translate(0, 0.15, 0); return g; }
    const g = new THREE.SphereGeometry(0.09, 10, 5, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2); // a clay diya
    g.scale(1, 0.6, 1); g.translate(0, 0.055, 0);
    return g;
  }, base, true);
  const flameMesh = useInstanced(() => { const g = new THREE.ConeGeometry(0.03, 0.1, 6); g.translate(0, 0.05, 0); return g; }, flames, false);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    flames.forEach((f, i) => {
      dummy.position.copy(f.pos);
      dummy.rotation.set(Math.sin(t * 9 + f.ph) * 0.12, 0, Math.cos(t * 7 + f.ph) * 0.12);
      dummy.scale.set(1, 0.85 + 0.3 * Math.abs(Math.sin(t * 11 + f.ph)), 1);
      dummy.updateMatrix();
      flameMesh.setMatrixAt(i, dummy.matrix);
    });
    flameMesh.instanceMatrix.needsUpdate = true;
  });
  return <><primitive object={bowls} /><primitive object={flameMesh} /></>;
};

// ------------------------------------------------------------------ kites / carp streamers
const kiteGeometry = (carp) => {
  if (carp) { // koinobori: a windsock-like fish, open at the mouth
    const body = new THREE.CylinderGeometry(0.55, 0.25, 3.2, 12, 1, true);
    body.rotateZ(Math.PI / 2);
    const tail = new THREE.ConeGeometry(0.5, 0.7, 4, 1, true); tail.rotateZ(-Math.PI / 2); tail.translate(1.9, 0, 0);
    return mergeGeometries([body, tail]);
  }
  const kite = new THREE.BufferGeometry();
  kite.setAttribute('position', new THREE.Float32BufferAttribute([0, 0.9, 0, -0.6, 0, 0, 0, -0.9, 0, 0, 0.9, 0, 0, -0.9, 0, 0.6, 0, 0], 3));
  kite.computeVertexNormals();
  const tail = new THREE.PlaneGeometry(0.05, 2.4); tail.translate(0, -2.1, 0);
  return mergeGeometries([kite, tail]);
};
const Kites = ({ palette, carp, weatherRef }) => {
  const items = useMemo(() => {
    const c = colors(palette);
    return Array.from({ length: carp ? 9 : 12 }, (_, i) => {
      const a = (i / (carp ? 9 : 12)) * Math.PI * 2 + rand(-0.2, 0.2), r = rand(20, 38);
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      return { pos: new THREE.Vector3(x, groundHeight(x, z) + rand(carp ? 7 : 11, carp ? 11 : 22), z), color: c[i % c.length], ph: Math.random() * 6 };
    });
  }, [palette, carp]);
  const mesh = useInstanced(() => kiteGeometry(carp), items, false);
  useFrame((state) => {
    const t = state.clock.elapsedTime, w = weatherRef.current;
    const windAngle = Math.atan2(-(w.windToZ || 0), w.windToX || 1); // carp stream downwind
    const gust = 0.6 + (w.wind || 0) * 0.8;
    items.forEach((k, i) => {
      dummy.position.set(k.pos.x + Math.sin(t * 0.6 + k.ph) * 1.2 * gust, k.pos.y + Math.sin(t * 0.9 + k.ph * 2) * 0.8 * gust, k.pos.z);
      if (carp) dummy.rotation.set(Math.sin(t * 3 + k.ph) * 0.15 * gust, windAngle, Math.sin(t * 2.2 + k.ph) * 0.1);
      else dummy.rotation.set(0, Math.atan2(-k.pos.x, -k.pos.z), Math.sin(t * 1.3 + k.ph) * 0.35 * gust); // face the hall, swaying
      dummy.scale.setScalar(carp ? 1.8 : 1.5);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
  });
  return <primitive object={mesh} />;
};

// ------------------------------------------------------------------ pumpkins or eggs on the lawn
const OUTSIDE = [
  ...HALL_WINDOWS.north.map((x) => ({ x, z: -12, nx: 0, nz: -1 })),
  ...HALL_WINDOWS.east.map((z) => ({ x: 12, z, nx: 1, nz: 0 })),
  ...HALL_WINDOWS.west.map((z) => ({ x: -12, z: -z, nx: -1, nz: 0 })),
];
const pumpkinGeometry = () => {
  const g = new THREE.SphereGeometry(0.4, 20, 12);
  const p = g.attributes.position, v = new THREE.Vector3();
  for (let i = 0; i < p.count; i++) { // ribs
    v.fromBufferAttribute(p, i);
    const k = 1 + 0.08 * Math.cos(Math.atan2(v.z, v.x) * 8);
    p.setXYZ(i, v.x * k, v.y * 0.72, v.z * k);
  }
  g.computeVertexNormals();
  g.translate(0, 0.27, 0);
  const stem = new THREE.CylinderGeometry(0.04, 0.06, 0.18, 6); stem.translate(0, 0.62, 0);
  return mergeGeometries([g, stem]);
};
const eggGeometry = () => { const g = new THREE.SphereGeometry(0.16, 14, 10); g.scale(1, 1.35, 1); g.translate(0, 0.17, 0); return g; };
const Lawn = ({ kind, nightRef }) => {
  const items = useMemo(() => {
    const pumpkins = kind === 'pumpkins';
    const c = colors(pumpkins ? ['#ff7b00', '#f77f00', '#ff8800', '#e85d04'] : ['#ffadad', '#ffd6a5', '#fdffb6', '#caffbf', '#9bf6ff', '#bdb2ff', '#ffc6ff']);
    const out = [];
    OUTSIDE.forEach((s, w) => {
      for (let i = 0; i < (pumpkins ? 3 : 4); i++) {
        const out_ = rand(1.6, 5.5), lat = rand(-2.2, 2.2);
        const x = s.x + s.nx * out_ - s.nz * lat, z = s.z + s.nz * out_ + s.nx * lat;
        out.push({ pos: new THREE.Vector3(x, groundHeight(x, z) - 0.03, z), color: c[(w * 3 + i) % c.length], ry: rand(0, 6), rz: pumpkins ? 0 : rand(-0.4, 0.4), s: pumpkins ? rand(0.8, 1.25) : rand(0.85, 1.15) });
      }
    });
    return out;
  }, [kind]);
  const mesh = useInstanced(kind === 'pumpkins' ? pumpkinGeometry : eggGeometry, items, true, { emissive: new THREE.Color('#ff6a00'), emissiveIntensity: 0 });
  useFrame(() => { if (kind === 'pumpkins') mesh.material.emissiveIntensity = 0.7 * nightRef.current; }); // jack-o'-lanterns glow at night
  return <primitive object={mesh} />;
};

// ------------------------------------------------------------------ Christmas tree
const TREE_AT = new THREE.Vector3(9.4, 0, 9.4); // the south-east corner, near the glass door
const ChristmasTree = () => {
  const tiers = useMemo(() => [
    { pos: TREE_AT.clone().setY(0.35), color: new THREE.Color('#5a3a22'), s: 0.35 },   // trunk
    { pos: TREE_AT.clone().setY(1.5), color: new THREE.Color('#1b5e20'), s: 1.6 },
    { pos: TREE_AT.clone().setY(2.6), color: new THREE.Color('#2e7d32'), s: 1.25 },
    { pos: TREE_AT.clone().setY(3.55), color: new THREE.Color('#388e3c'), s: 0.9 },
  ], []);
  const baubles = useMemo(() => {
    const c = colors(['#d00000', '#ffd166', '#e5e5e5', '#4895ef', '#d00000']);
    return Array.from({ length: 34 }, (_, i) => {
      const h = rand(0.9, 3.9), r = (4.3 - h) * 0.42 + 0.05, a = i * 2.4;
      return { pos: new THREE.Vector3(TREE_AT.x + Math.cos(a) * r, h, TREE_AT.z + Math.sin(a) * r), color: c[i % c.length] };
    });
  }, []);
  const lights = useMemo(() => Array.from({ length: 60 }, (_, i) => {
    const f = i / 60, h = 0.8 + f * 3.3, r = (4.3 - h) * 0.45 + 0.08, a = f * Math.PI * 14;
    return { pos: new THREE.Vector3(TREE_AT.x + Math.cos(a) * r, h, TREE_AT.z + Math.sin(a) * r), color: new THREE.Color(i % 3 ? '#ffe8a3' : '#ff8fa3'), ph: Math.random() * 6 };
  }).concat([{ pos: TREE_AT.clone().setY(4.45), color: new THREE.Color('#ffd166'), ph: 0, star: true }]), []);
  const treeMesh = useInstanced(() => { const g = new THREE.ConeGeometry(1, 1.4, 14); return g; }, tiers, true, { roughness: 0.8 });
  const baubleMesh = useInstanced(() => new THREE.SphereGeometry(0.09, 10, 8), baubles, true);
  const lightMesh = useInstanced(() => new THREE.OctahedronGeometry(0.04), lights, false);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    lights.forEach((l, i) => {
      dummy.position.copy(l.pos);
      dummy.rotation.set(0, l.star ? t * 0.6 : 0, 0);
      dummy.scale.setScalar(l.star ? 4.5 : 0.7 + 0.5 * Math.max(0, Math.sin(t * 2.5 + l.ph)));
      dummy.updateMatrix();
      lightMesh.setMatrixAt(i, dummy.matrix);
    });
    lightMesh.instanceMatrix.needsUpdate = true;
  });
  return <><primitive object={treeMesh} /><primitive object={baubleMesh} /><primitive object={lightMesh} /></>;
};

// ------------------------------------------------------------------ flower carpet / rangoli
const CARPET_AT = new THREE.Vector3(0, 0.015, 6.6); // between the statue and the glass door
const Carpet = ({ palette }) => {
  const items = useMemo(() => {
    const c = colors(palette), out = [];
    out.push({ pos: CARPET_AT.clone(), color: c[0], s: 2.2 });
    for (let ring = 1; ring <= 7; ring++) {
      const r = ring * 0.26, n = 8 * ring;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + (ring % 2) * (Math.PI / n);
        out.push({ pos: new THREE.Vector3(CARPET_AT.x + Math.cos(a) * r, CARPET_AT.y + ring * 0.0004, CARPET_AT.z + Math.sin(a) * r), color: c[ring % c.length], ry: -a, s: 1 + ring * 0.08 });
      }
    }
    return out;
  }, [palette]);
  const mesh = useInstanced(() => {
    const g = new THREE.CircleGeometry(0.15, 10); // a petal: a stretched disc lying on the floor
    g.scale(1, 0.5, 1); g.rotateX(-Math.PI / 2);
    return g;
  }, items, false);
  return <primitive object={mesh} />;
};

// ------------------------------------------------------------------ timing
// Petals fall for half a minute after arriving; fireworks light up the first half minute and then
// every night; balloons rise all day.
const FestivalDriver = ({ festival, nightRef }) => {
  const st = useRef({ t0: null });
  useEffect(() => {
    eggState.confettiColors = festival.petals ? colors(festival.petals) : null;
    eggState.fireworksColors = festival.fireworks ? colors(festival.fireworks) : null;
    eggState.balloonColors = festival.balloons ? colors(festival.balloons) : null;
    if (festival.balloons) eggState.celebrate = true;
    return () => { eggState.confettiColors = eggState.fireworksColors = eggState.balloonColors = null; };
  }, [festival]);
  useFrame(() => {
    const now = performance.now(), s = st.current;
    if (s.t0 === null) {
      s.t0 = now;
      if (festival.petals) eggState.confettiUntil = now + 3000 + 30000;
      if (festival.fireworks) eggState.fireworksUntil = Math.max(eggState.fireworksUntil, now + 30000);
    }
    if (festival.fireworks && nightRef.current > 0.6) eggState.fireworksUntil = Math.max(eggState.fireworksUntil, now + 1500);
  });
  return null;
};

export const Festivals = ({ festival, nightRef, weatherRef }) => {
  if (!festival) return null;
  return (
    <group>
      <FestivalDriver festival={festival} nightRef={nightRef} />
      {festival.lanterns && <Lanterns palette={festival.lanterns} paper={festival.id === 'tanabata'} nightRef={nightRef} besideTree={!!festival.tree} />}
      {festival.lamps && <Lamps candles={festival.id === 'hanukkah' || festival.id === 'muertos'} />}
      {festival.kites && <Kites palette={festival.kites} carp={festival.id === 'koinobori'} weatherRef={weatherRef} />}
      {festival.lawn && <Lawn kind={festival.lawn} nightRef={nightRef} />}
      {festival.tree && <ChristmasTree />}
      {festival.carpet && <Carpet palette={festival.carpet} />}
    </group>
  );
};

export default Festivals;
